import React from 'react';
import { useTranslation } from 'react-i18next';
import SectionCard from './SectionCard';
import VerbatimQuote from './VerbatimQuote';
import RecoBadge from './RecoBadge';
import { categoryEmoji, categoryLabel, typeBadge } from '../utils/insights';

export default function CategoryCard({ group, onTap }) {
  const { t } = useTranslation('tabs');
  if (!group) return null;
  const { category, insights, score, dominantType, totalVerbatims } = group;
  const badge = typeBadge(dominantType, t);
  const primaryAction = insights.find(i => i.action)?.action || null;

  // Récupère 2 verbatims en aplat depuis tous les insights du groupe
  const verbatims = [];
  for (const i of insights) {
    if (Array.isArray(i.verbatims)) {
      for (const v of i.verbatims) {
        if (verbatims.length < 2 && v) verbatims.push(v);
      }
    }
    if (verbatims.length >= 2) break;
  }

  const handleTap = () => {
    if ('vibrate' in navigator) navigator.vibrate(5);
    if (typeof onTap === 'function') onTap(group);
  };

  return (
    <button type="button" onClick={handleTap} style={styles.btn} aria-label={t('insights.category_aria', { name: categoryLabel(category, t) })}>
      <SectionCard
        title={
          <span style={styles.titleRow}>
            <span style={styles.emoji} aria-hidden>{categoryEmoji(category)}</span>
            <span style={styles.label}>{categoryLabel(category, t)}</span>
            <span style={styles.score}>{score}/100</span>
          </span>
        }
        action={<RecoBadge variant={badge.variant} label={badge.label} />}
        style={styles.card}
      >
        {verbatims.length > 0 ? (
          <div style={styles.verbatims}>
            {verbatims.map((v, idx) => <VerbatimQuote key={idx} text={v} />)}
          </div>
        ) : (
          <p style={styles.noVerbatim}>Aucun verbatim disponible.</p>
        )}

        {primaryAction && (
          <p style={styles.action}><strong>Reco :</strong> {primaryAction}</p>
        )}

        <div style={styles.footer}>
          <span>{insights.length} insight{insights.length > 1 ? 's' : ''} · {totalVerbatims} verbatim{totalVerbatims > 1 ? 's' : ''}</span>
          <span style={styles.tapHint}>Voir tout →</span>
        </div>
      </SectionCard>
    </button>
  );
}

const styles = {
  btn: {
    background: 'transparent',
    border: 'none',
    padding: 0,
    margin: 0,
    width: '100%',
    textAlign: 'left',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  card: {
    width: '100%',
  },
  titleRow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  },
  emoji: {
    fontSize: 18,
    lineHeight: 1,
  },
  label: {
    fontSize: 17,
    fontWeight: 700,
    color: '#1A1A1A',
  },
  score: {
    fontSize: 13,
    color: '#6B6B6B',
    fontWeight: 600,
  },
  verbatims: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  noVerbatim: {
    margin: 0,
    fontSize: 13,
    color: '#9A9A9A',
    fontStyle: 'italic',
  },
  action: {
    margin: 0,
    fontSize: 13,
    color: '#1A1A1A',
    lineHeight: 1.5,
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 12,
    color: '#6B6B6B',
    paddingTop: 8,
    borderTop: '1px solid #EDEAE3',
  },
  tapHint: {
    color: '#E8502A',
    fontWeight: 600,
  },
};
